import React, { useState, useCallback } from 'react';
import {
  IParticipantForUser,
  IPartyForUser,
  StreamType,
  TAreaKey,
  TUserId,
} from '../../PartyClientResponse';
import { configureArea } from '../../controller/client/PartyClient';
import { getIdToken } from '../../controller/PartyDB';
import { getAreaInfo, IAreaInfo } from '../../controller/Areas';
import { useVideoController } from '../../controller/video/VideoControlContext';
import ParticipantBubble from './ParticipantBubble';
import { Form, Switch, Tooltip } from 'antd';
import { useAppState } from '../../state/AppState';
import InviteUserModal from './InviteUserModal';
import Modal from '../common/Modal';
import RoomNameFormItem from '../common/forms/items/RoomNameFormItem';
import OkCancelButtons from '../common/forms/OkCancelButtons';
import { GAMES_ENABLED } from './AppConstants';
import { useGameState } from '../game/GameState';
import useParticipantPositioning from './useParticipantPositioning';

export function random(min: number, max: number, intResult?: boolean) {
  if (typeof min !== 'number' && typeof max !== 'number') {
    min = 0;
    max = 1;
  }

  if (typeof max !== 'number') {
    max = min;
    min = 0;
  }

  let result = min + Math.random() * (max - min);

  if (intResult) {
    result = parseInt(String(result), 10);
  }

  return result;
}

function EditAreaModalContent({
  area,
  party,
  onSubmit,
  onCancel,
}: {
  area?: IAreaInfo;
  party: IPartyForUser;
  onCancel: () => void;
  onSubmit: () => void;
}) {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const submit = useCallback(
    async ({ name, ...rest }: { name: string; private: boolean }) => {
      setLoading(true);
      try {
        await configureArea({
          idToken: await getIdToken(),
          key: party.key,
          area: area?.key as TAreaKey,
          settings: {
            name,
            private: rest.private,
          },
        });
        onSubmit();
      } catch (err) {
        console.log(err);
        setError('There was an unknown error. please try again');
        setLoading(false);
      }
      setLoading(false);
    },
    [onSubmit, area?.key, party.key]
  );

  return (
    <div className='onboarding-card'>
      <h1>edit your room</h1>
      <Form
        preserve={false}
        style={{ width: '100%' }}
        hideRequiredMark
        name='values'
        layout='vertical'
        initialValues={{ roomname: area?.name || '', private: area?.settings?.private || false }}
        onFinish={(values) => {
          submit({ name: values.roomname, private: values.private });
        }}>
        <RoomNameFormItem
          name='roomname'
          formItemProps={{ label: 'name' }}
          inputProps={{ autoFocus: true }}
        />
        <Form.Item label='Private room?' name='private' valuePropName='checked'>
          <Switch loading={loading} />
        </Form.Item>
        {!!error && <div style={{ marginBottom: 12 }}>{error}</div>}
        <OkCancelButtons
          loading={loading}
          onCancel={() => onCancel()}
          okText='save'
          cancelText='cancel'
        />
      </Form>
    </div>
  );
}

const CurrentArea = ({
  participants,
  currentParticipant,
  party,
  allParticipants,
  setSizing,
}: {
  participants: IParticipantForUser[];
  currentParticipant: IParticipantForUser;
  party: IPartyForUser;
  allParticipants: IParticipantForUser[];
  setSizing: ({
    width,
    height,
    offsetLeft,
    offsetTop,
  }: {
    width: number;
    height: number;
    offsetTop: number;
    offsetLeft: number;
  }) => void;
}) => {
  const { remoteStreams } = useVideoController();
  const { user, spotlightPosition, mainVideoPosition } = useAppState();

  const { createGame } = useGameState();

  const [openModal, setOpenModal] = useState<'' | 'invite'>('');
  const [isEditAreaModalOpen, setIsEditAreaModalOpen] = useState<boolean>(false);
  const handleInvitePeople = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setOpenModal('invite');
  };
  const handleCloseModal = () => {
    setOpenModal('');
  };

  const currentParticipantArea = ((currentParticipant && currentParticipant.currentArea) ||
    '') as TAreaKey;
  const currentAreaFeatures = getAreaInfo(currentParticipantArea, party);
  const { ref, positionMap } = useParticipantPositioning(
    participants,
    user,
    setSizing,
    currentAreaFeatures.shouldShowArea
  );

  const isHost = user?.isAdmin || currentParticipant?.tags?.includes('host');

  const spotlightedParticipantId = party.stage.spotlight?.participant?.uid;
  const areaPlusSpotlightedParticipantIds = allParticipants
    // .filter((p) => p.uid !== user?.uid)
    .filter((p) => {
      // Always include self since we attach the self-vid here
      if (p.uid === currentParticipant.uid) {
        return true;
      }

      if (p.currentArea === currentParticipantArea && currentAreaFeatures.shouldShowArea) {
        return true;
      }

      if (p.uid === spotlightedParticipantId) {
        return true;
      }

      if (
        party.stage.stream.type === StreamType.Participant &&
        p.uid === party.stage.stream.mainParticipantId
      ) {
        return true;
      }

      return false;
    });

  // We have all this ugly code so that React knows that all participant videos are generated here
  // This allows us to animate them as they move to spotlight or main screen
  // The key prop staying the same prevents React from unmounting etc
  const participantBubbles = areaPlusSpotlightedParticipantIds.map((p) => {
    // Self video
    if (user?.uid === p.uid) {
      const isMainVideo =
        party.stage?.stream?.type === StreamType.Participant &&
        p.uid === party?.stage?.stream?.mainParticipantId;

      const isSpotlistedParticipant = p.uid === spotlightedParticipantId;

      const style: React.CSSProperties = {
        position: 'fixed',
      };

      let radius = 0;
      if (isMainVideo) {
        radius = Math.min(window.innerWidth, window.innerHeight) / 2;
        style.left = 0;
        style.top = 0;
        style.width = window.innerWidth;
        style.height = window.innerHeight;
        style.borderRadius = 0;
        style.border = 'none';
        style.zIndex = 1;
        style.pointerEvents = 'none';
        style.position = 'fixed';
      } else if (isSpotlistedParticipant) {
        radius = Math.round(Math.min(spotlightPosition.width, spotlightPosition.height) / 2) || 0;

        // centers spotlight
        const extraOffsetLeft =
          spotlightPosition.width > radius ? (spotlightPosition.width - radius) / 2 : 0;

        style.left = extraOffsetLeft + spotlightPosition.left;
        style.top = spotlightPosition.top;
        style.width = radius * 2;
        style.height = radius * 2;
        style.borderRadius = 30;
        style.border = 'none';
      } else {
        const SIZE = 80;
        radius = SIZE / 2;
        style.width = SIZE;
        style.height = SIZE;
        style.position = 'absolute';
        style.bottom = 54;
        style.right = 12;
        style.borderRadius = '50%';
      }

      return (
        <ParticipantBubble
          party={party}
          radius={radius}
          style={style}
          left={0}
          top={0}
          key={user.uid}
          participantId={user.uid}
          audio={'muted'}
          square={isMainVideo}
          // stream={remoteStreams[p.uid]}
          userIsHost={currentParticipant.tags && currentParticipant.tags.includes('host')}
        />
      );
    }

    // Main video participant
    if (
      party.stage.stream.type === StreamType.Participant &&
      p.uid === party.stage.stream.mainParticipantId
    ) {
      // Handled in MainVideoPlayer for performance reasons
      return null;
    }

    // Spotlighted participant
    if (p.uid === spotlightedParticipantId) {
      const radius =
        Math.round(Math.min(spotlightPosition.width, spotlightPosition.height) / 2) || 0;

      // centers spotlight
      const extraOffsetLeft =
        spotlightPosition.width > radius ? (spotlightPosition.width - radius) / 2 : 0;

      const circle = {
        circleRadius: radius,
        position: {
          x: extraOffsetLeft + spotlightPosition.left - (ref.current?.offsetLeft || 0),
          y: spotlightPosition.top - (ref.current?.offsetTop || 0),
        },
      };

      if (spotlightPosition.ref?.current) {
        return (
          <ParticipantBubble
            party={party}
            radius={radius}
            left={circle?.position.x || 0}
            top={circle?.position.y || 0}
            key={p.uid}
            participantId={p.uid}
            audio={
              (currentParticipantArea === p.currentArea && currentAreaFeatures.audio) || 'muted'
            }
            stream={remoteStreams[spotlightedParticipantId as TUserId]}
            userIsHost={currentParticipant.tags && currentParticipant.tags.includes('host')}
          />
        );
      }
    }

    // Area participants
    const circle = positionMap[p.uid] || {
      circleRadius: 10,
      position: {
        x: 0,
        y: 0,
      },
    };
    return (
      <ParticipantBubble
        party={party}
        radius={Math.round(circle?.circleRadius || 0)}
        left={Math.round(circle?.position.x - (circle?.circleRadius || 0))}
        top={Math.round(circle?.position.y - (circle?.circleRadius || 0))}
        key={p.uid}
        participantId={p.uid}
        audio={(currentParticipantArea === p.currentArea && currentAreaFeatures.audio) || 'muted'}
        stream={remoteStreams[p.uid]}
        userIsHost={currentParticipant.tags && currentParticipant.tags.includes('host')}
      />
    );
  });

  return (
    <div className='current-area' ref={ref}>
      {/* <CSSTransition in={showTransition} timeout={500} appear classNames='current-area-transition'> */}
      <div className='current-area-background'>
        <>
          <div className='current-area-top-row'>
            <div className='current-area-button-group'>
              {/* {!!currentAreaFeatures.currentGameId && (
                <div className='game-started'>game started</div>
              )} */}
              <Modal
                onRequestClose={() => {
                  setIsEditAreaModalOpen(false);
                }}
                isOpen={isEditAreaModalOpen}>
                <EditAreaModalContent
                  onCancel={() => setIsEditAreaModalOpen(false)}
                  onSubmit={() => setIsEditAreaModalOpen(false)}
                  area={currentAreaFeatures}
                  party={party}
                />
              </Modal>
              <Tooltip
                title={'Invite user to room'}
                arrowPointAtCenter
                mouseEnterDelay={1}
                placement='top'>
                <img
                  onClick={handleInvitePeople}
                  alt='invite user'
                  className='current-area-button'
                  src='/icons/invite_user.svg'></img>
              </Tooltip>
              {(isHost || currentAreaFeatures.ownerId === user?.uid) && (
                <>
                  <Tooltip
                    title={'Edit room settings'}
                    arrowPointAtCenter
                    mouseEnterDelay={1}
                    placement='top'>
                    <img
                      onClick={() => setIsEditAreaModalOpen(true)}
                      alt='edit room settings'
                      className='current-area-button'
                      src='/icons/settings_room.svg'></img>
                  </Tooltip>
                  {GAMES_ENABLED && (isHost || currentAreaFeatures.ownerId === user?.uid) && (
                    <img
                      onClick={createGame}
                      alt='toggle game view'
                      className='current-area-button'
                      src='/icons/star.svg'></img>
                  )}
                </>
              )}
            </div>
            <div className='current-area-name'>{currentAreaFeatures.name}</div>
          </div>
          {currentAreaFeatures.roomText && (
            <div className='current-area-room-text'>
              <currentAreaFeatures.roomText />
            </div>
          )}
          {!!currentParticipantArea && participantBubbles}
        </>
      </div>
      {/* </CSSTransition> */}
      <div className='my-participant-bubble' />
      <div id='bigboy' />
      <InviteUserModal
        isOpen={openModal === 'invite'}
        onRequestClose={handleCloseModal}
        participants={allParticipants}
        party={party}
        user={user}
      />
    </div>
  );
};

export default CurrentArea;
